import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { invalidateRegistrationWizardMetadata } from '@/admin/lib/wizard-metadata-cache'
import type { HostTemplate, HostTemplateSeed } from '@/admin/types/hosts'
import type { PaginatedResponse } from '@/shared/types/api'

// ── Templates ────────────────────────────────────────────

export function useHostTemplates(options: { enabled?: boolean } = {}) {
  return useQuery<HostTemplate[]>({
    queryKey: ['host-templates'],
    queryFn: () =>
      api
        .get<PaginatedResponse<HostTemplate>>('/hosts/templates/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

export function useHostTemplate(uuid: string | null) {
  return useQuery<HostTemplate>({
    queryKey: ['host-template', uuid],
    queryFn: () => api.get(`/hosts/templates/${uuid}/`).then(r => r.data),
    enabled: !!uuid,
  })
}

export function useHostTemplateSeeds() {
  return useQuery<HostTemplateSeed[]>({
    queryKey: ['host-template-seeds'],
    queryFn: () => api.get('/hosts/templates/seeds/').then(r => r.data),
  })
}

export function useCreateHostTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      slug: string
      description?: string
      icon?: string
      seed_kind?: string
      identity_attributes?: string[]
    }) => api.post('/hosts/templates/', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['host-templates'] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

export function useUpdateHostTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...data
    }: {
      uuid: string
      name?: string
      slug?: string
      description?: string
      icon?: string
      identity_attributes?: string[]
    }) => api.patch(`/hosts/templates/${uuid}/`, data).then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['host-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({ queryKey: ['host-template', vars.uuid] })
    },
  })
}

export function useDeleteHostTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/hosts/templates/${uuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['host-templates'] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

// ── Attributes ───────────────────────────────────────────

export function useCreateHostAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      ...data
    }: {
      templateUuid: string
      code_name: string
      display_name: string
      field_type: string
      required?: boolean
      order?: number
      maps_to_column?: string
      lookup_provider?: string
      is_prepopulated?: boolean
      visibility?: 'PUBLIC' | 'PARTNER' | 'INTERNAL'
      configuration_data?: Record<string, unknown>
    }) =>
      api
        .post(`/hosts/templates/${templateUuid}/attributes/`, data)
        .then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['host-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({ queryKey: ['host-template', vars.templateUuid] })
    },
  })
}

export function useUpdateHostAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attributeUuid,
      ...data
    }: {
      templateUuid: string
      attributeUuid: string
      display_name?: string
      description?: string
      required?: boolean
      order?: number
      lookup_provider?: string
      is_prepopulated?: boolean
      visibility?: 'PUBLIC' | 'PARTNER' | 'INTERNAL'
      configuration_data?: Record<string, unknown>
    }) =>
      api
        .patch(
          `/hosts/templates/${templateUuid}/attributes/${attributeUuid}/`,
          data,
        )
        .then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['host-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({ queryKey: ['host-template', vars.templateUuid] })
    },
  })
}

export function useReorderHostAttributes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attributes,
    }: {
      templateUuid: string
      attributes: { uuid: string }[]
    }) =>
      api.patch(`/hosts/templates/${templateUuid}/attributes/reorder/`, {
        attributes,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['host-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({ queryKey: ['host-template', vars.templateUuid] })
    },
  })
}

// ── Consumer hosts (existing host selection) ────────────

export interface ConsumerHostSummary {
  uuid: string
  kind: string
  name: string
  registration_number: string
  vin: string
  address: import('@/shared/lib/formatting').Address | null
  host_template: { uuid: string; name: string; slug: string; icon: string; seed_kind: string } | null
  attribute_values: { code_name: string; display_name: string; value: unknown }[]
}

export function useConsumerHosts(
  email: string | null,
  hostTemplateUuid: string | null,
  partnershipUuid?: string | null,
) {
  return useQuery<ConsumerHostSummary[]>({
    queryKey: ['consumer-hosts', email, hostTemplateUuid, partnershipUuid ?? ''],
    queryFn: () =>
      api
        .get('/hosts/by-consumer/', {
          params: {
            email,
            host_template_uuid: hostTemplateUuid,
            // The registrant's own email offers no property to pick.
            partnership_uuid: partnershipUuid || undefined,
          },
        })
        .then((r) => r.data),
    enabled: !!email && !!hostTemplateUuid,
  })
}

export function useDeleteHostAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attributeUuid,
      force,
    }: {
      templateUuid: string
      attributeUuid: string
      force?: boolean
    }) =>
      api.delete(`/hosts/templates/${templateUuid}/attributes/${attributeUuid}/`, {
        params: force ? { force: 'true' } : undefined,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['host-templates'] })
      invalidateRegistrationWizardMetadata(qc)
      qc.invalidateQueries({ queryKey: ['host-template', vars.templateUuid] })
    },
  })
}
